import { useCreateCheckIn } from './useCheckIns';
import { useAssignLockerKey } from './useLockers';
import { useToastContext } from './useToastContext';
import { isCheckInCooldownError, CHECK_IN_COOLDOWN_MESSAGE, logger } from '@/lib';
import type { MemberDirectoryEntry } from '@/types';

interface SubmitArgs {
  /** The directory row is enough: only id, name and email are read. */
  member: Pick<MemberDirectoryEntry, 'id' | 'first_name' | 'last_name' | 'email'>;
  /** Free locker to hand out with this check-in; assigned only after the check-in succeeds. */
  lockerId?: string;
  /**
   * Fires once the CHECK-IN itself succeeded (regardless of the locker outcome) —
   * call sites close their modal and run per-site invalidations here. In
   * `keyOnly` mode there is no check-in, so it fires once the KEY handout
   * succeeded instead.
   */
  onSuccess?: () => void;
  /**
   * Hand out the key against the member's EXISTING check-in instead of creating
   * a second one. Set when the member is already checked in today and the admin
   * only came back to the modal for a locker — the exact sequence that used to
   * mint duplicate check-in rows. Requires `lockerId`.
   */
  keyOnly?: boolean;
}

/**
 * The one check-in submit sequence shared by every CheckInModal host
 * (CheckInsPage, MemberViewPage, Layout header): check in first, then — only if
 * that succeeded — optionally hand out a locker key. Activity logging and query
 * invalidation live inside the wrapped mutations; this hook only sequences and
 * toasts. A failed key handout after a successful check-in is surfaced as a
 * partial success (the member IS in the gym) — never rolled back.
 *
 * `keyOnly` inverts that: no check-in is created at all and the key is handed
 * out against the one the member already has. See GiveLockerKeyModal for the
 * standalone member-first route to the same handout.
 */
export function useCheckInWithLocker() {
  const createCheckIn = useCreateCheckIn();
  const assignKey = useAssignLockerKey();
  const { showToast } = useToastContext();

  // Every await is inside try/catch so no rejection ever escapes to
  // window.onunhandledrejection (Sentry HOK-OS-2W: raw HK429 cooldown object
  // captured as an unhandled rejection).
  const submit = async ({ member, lockerId, onSuccess, keyOnly }: SubmitArgs): Promise<void> => {
    const memberName = `${member.first_name} ${member.last_name}`;

    // Key-only: the member is already checked in, so the handout rides that
    // existing check-in and nothing is inserted into check_ins. A failure here
    // is total (no check-in was created to partially succeed), so the modal is
    // left open and onSuccess never fires.
    if (keyOnly) {
      if (!lockerId) {
        logger.error('useCheckInWithLocker: keyOnly submit with no lockerId — ignoring');
        return;
      }
      try {
        await assignKey.mutateAsync({ locker_id: lockerId, member_id: member.id });
      } catch (error) {
        showToast(
          `Could not give out the locker key: ` +
            `${error instanceof Error ? error.message : 'Unknown error.'}`,
          'error'
        );
        return;
      }
      showToast(`Locker key given out to ${memberName}`, 'success');
      try {
        onSuccess?.();
      } catch (err) {
        logger.error('useCheckInWithLocker onSuccess callback failed', err);
      }
      return;
    }

    try {
      await createCheckIn.mutateAsync({
        memberId: member.id,
        memberName,
        memberEmail: member.email || '',
      });
    } catch (error) {
      showToast(
        isCheckInCooldownError(error) ? CHECK_IN_COOLDOWN_MESSAGE : 'Failed to check in member',
        'error'
      );
      return; // Locker is never attempted; the modal stays open.
    }

    // Guarded like every await above: a host's onSuccess (modal close, query
    // invalidation) throwing would otherwise escape submit() as an unhandled
    // rejection — the exact failure mode this hook exists to prevent.
    try {
      onSuccess?.();
    } catch (err) {
      logger.error('useCheckInWithLocker onSuccess callback failed', err);
    }

    if (!lockerId) {
      showToast(`${memberName} checked in successfully`, 'success');
      return;
    }

    try {
      await assignKey.mutateAsync({ locker_id: lockerId, member_id: member.id });
      showToast(`${memberName} checked in — locker key given out`, 'success');
    } catch (error) {
      // Partial success: the check-in stands, only the key handout failed
      // (e.g. 23505 race — useAssignLockerKey already maps it to a readable message).
      showToast(
        `${memberName} checked in, but the locker key could not be given out: ` +
          `${error instanceof Error ? error.message : 'Unknown error.'} ` +
          'Assign a key from the Lockers page.',
        'error'
      );
    }
  };

  return {
    submit,
    isPending: createCheckIn.isPending || assignKey.isPending,
  };
}
